import { UsersAction } from './../users/users.actions';
import { UnauthorizedErrorHandlerService } from './../../services/unauthorized-error-handler/unauthorized-error-handler.service';
import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { BackendService } from '../../services/backend/backend.service';
import { DefaultResponse } from '../../models/form/DefaultResponse';
import { EnvironmentAction } from './environment.action';
import { RoutesTable } from '../../resources/routes-table.model';
import { Observer, Observable, of } from 'rxjs';
import {
  withLatestFrom,
  exhaustMap,
  map,
  filter,
  share,
  retryWhen,
  delay,
  take,
  takeUntil,
  catchError
} from 'rxjs/operators';
import { SettingsService } from '../../services/settings/settings.service';
import { AppState } from '../../interfaces/app-state.interface';
import { Store } from '@ngrx/store';
import { UsersEffects } from '../users/users.effects';

@Injectable()
export class EnvironmentEffects {
  /** Recupera a lista de environments a serem usados pelo usuário */
  @Effect()
  public getEnvironments$ = this.actions$.pipe(
    ofType(EnvironmentAction.getEnvironments),
    withLatestFrom(
      this.store$.select(store => store.authentication.currentUser)
    ),
    exhaustMap(([action, currentUser]) => {
      if (!currentUser || !currentUser.token) {
        return of({
          type: EnvironmentAction.getEnvironmentsError,
          payload: 'Environments are only avaliable to authenticated users.'
        });
      }

      const url = RoutesTable.composeUri(
        RoutesTable.environment.routes.getEnvironments,
        this.settingsService.getConfigurationKeyValue('ci')
      );

      return this.backendService.get<DefaultResponse>(url)
      .pipe(
        map(result => {
          if (result.ok) {
            if (
              typeof result.value !== 'object' ||
              typeof result.value[0] !== 'string'
            ) {
              return {
                type: EnvironmentAction.getEnvironmentsError,
                payload: 'Unexpected api result value'
              };
            }

            return {
              type: EnvironmentAction.getEnvironmentsSuccess,
              payload: result.value
            };
          }

          return {
            type: EnvironmentAction.getEnvironmentsError,
            payload: result.messages
          };
        }),
        // caso o usuário receba 401 e não realize a autenticação este request não deve continuar com o retry
        takeUntil(
          this.usersEffects.forceAuthenticationRequest$.pipe(
            filter(a => a.type.equals(UsersAction.forcedAuthenticationFailed))
          )
        ),
        retryWhen(error => {
          return Observable.create((observer: Observer<any>) => {
            error
              .pipe(
                delay(3000),
                take(100)
              )
              .subscribe(
                data => observer.next(data),
                () => {
                  console.error('Cant load environments');
                  observer.error(error);
                }
              );
          });
        }),
        catchError(error => {
          return of({
            type: EnvironmentAction.getEnvironmentsError,
            payload: error
          });
        })
      );
    }),
    share()
  );

  @Effect({ dispatch: false })
  public environmentsLoaded = this.actions$.pipe(
    ofType(EnvironmentAction.getEnvironmentsSuccess),
    withLatestFrom(this.store$, (action, store) => {
      return {
        action,
        environment: store.environment,
        currentUser: store.authentication.currentUser
      };
    }),
    filter(
      payload =>
        !payload.environment.currentEnvironment &&
        payload.environment.environments.length > 0 &&
        payload.environment.environmentUntouched &&
        payload.currentUser &&
        payload.currentUser.token
    ),
    map(() => {
      console.warn('Turning on environment switching...');
      this.store$.dispatch({
        type: EnvironmentAction.enableEnvironmentSwitching
      });
    })
  );

  @Effect({ dispatch: false })
  public resetEnvironment$ = this.actions$.pipe(
    ofType(EnvironmentAction.resetCurrentEnvironment)
  );

  /** Recupera a lista de environments a serem usados pelo usuário */
  @Effect()
  public getEnvironmentMachine$ = this.actions$.pipe(
    ofType(EnvironmentAction.getEnvironmentMachine),
    exhaustMap(action => {
      return this.backendService
        .get<DefaultResponse>(
          RoutesTable.environment.routes.getEnvironmentMachine.uri
        )
        .pipe(
          map(result => {
            return {
              type: EnvironmentAction.getEnvironmentMachineSuccess,
              payload: result
              // result.value => value does not exist on type {},
            };
          }),
          catchError(error => {
            return of({
              type: EnvironmentAction.getEnvironmentMachineError,
              payload: 'Unreacheable'
            });
          })
        );
    }),
    share()
  );

  /** Switch de ambiente */
  @Effect({ dispatch: false })
  public enableEnvironmentSwitcht$ = this.actions$.pipe(
    ofType(EnvironmentAction.enableEnvironmentSwitching),
    share()
  );

  @Effect({ dispatch: false })
  public disableEnvironmentSwitcht$ = this.actions$.pipe(
    ofType(EnvironmentAction.disableEnvironmentSwitching),
    share()
  );

  constructor(
    private actions$: Actions<EnvironmentAction>,
    private backendService: BackendService,
    private settingsService: SettingsService,
    private store$: Store<AppState>,
    private usersEffects: UsersEffects
  ) {}
}
